import React, { useEffect, useRef, useState } from 'react';
import { makeApiRequests } from '../helpers/api';
import { ENDPOINTS } from '../helpers/constants';
import './DevEnvIndicator.css';

const STORAGE_KEY = 'devEnvIndicatorPosition';
const DEFAULT_POSITION = { left: 16, top: null, right: null, bottom: 16 };

const loadPosition = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (saved && typeof saved.left === 'number' && typeof saved.top === 'number') {
      return { left: saved.left, top: saved.top, right: null, bottom: null };
    }
  } catch (e) {
    /* ignore */
  }
  return DEFAULT_POSITION;
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const DevEnvIndicator = () => {
  const [serverInfo, setServerInfo] = useState(null);
  const [position, setPosition] = useState(loadPosition);
  const [isDragging, setIsDragging] = useState(false);
  const elementRef = useRef(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const didDragRef = useRef(false);

  useEffect(() => {
    if (window.location.hostname !== 'localhost') return;
    makeApiRequests({ endpoint: ENDPOINTS.SERVER_INFO, method: 'GET' }).then(({ response }) => {
      if (response) setServerInfo(response);
    });
  }, []);

  useEffect(() => {
    if (!isDragging) return undefined;

    const handleMouseMove = (e) => {
      didDragRef.current = true;
      const el = elementRef.current;
      const width = el ? el.offsetWidth : 0;
      const height = el ? el.offsetHeight : 0;
      const maxLeft = window.innerWidth - width;
      const maxTop = window.innerHeight - height;
      const left = clamp(e.clientX - dragOffsetRef.current.x, 0, Math.max(0, maxLeft));
      const top = clamp(e.clientY - dragOffsetRef.current.y, 0, Math.max(0, maxTop));
      setPosition({ left, top, right: null, bottom: null });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setPosition((prev) => {
        if (typeof prev.left === 'number' && typeof prev.top === 'number') {
          try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ left: prev.left, top: prev.top }));
          } catch (e) {
            /* ignore */
          }
        }
        return prev;
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    const rect = elementRef.current.getBoundingClientRect();
    dragOffsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    didDragRef.current = false;
    setPosition({ left: rect.left, top: rect.top, right: null, bottom: null });
    setIsDragging(true);
    e.preventDefault();
  };

  const handleDoubleClick = () => {
    setPosition(DEFAULT_POSITION);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* ignore */
    }
  };

  if (!serverInfo) return null;

  const { dbEnvironment, isDevServer } = serverInfo;

  const style = {
    left: position.left != null ? `${position.left}px` : 'auto',
    top: position.top != null ? `${position.top}px` : 'auto',
    right: position.right != null ? `${position.right}px` : 'auto',
    bottom: position.bottom != null ? `${position.bottom}px` : 'auto',
  };

  return (
    <div
      ref={elementRef}
      className={`dev-env-indicator${isDragging ? ' dev-env-indicator--dragging' : ''}`}
      style={style}
    >
      <span
        className="dev-env-indicator__handle"
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
        title="Drag to move • Double-click to reset"
        aria-label="Drag handle"
        role="button"
      >
        <svg
          className="dev-env-indicator__handle-icon"
          width="10"
          height="14"
          viewBox="0 0 10 14"
          aria-hidden="true"
          focusable="false"
        >
          <circle cx="2" cy="2" r="1.2" />
          <circle cx="8" cy="2" r="1.2" />
          <circle cx="2" cy="7" r="1.2" />
          <circle cx="8" cy="7" r="1.2" />
          <circle cx="2" cy="12" r="1.2" />
          <circle cx="8" cy="12" r="1.2" />
        </svg>
      </span>
      <span className={`dev-env-indicator__dot dev-env-indicator__dot--${dbEnvironment}`} />
      <span className={`dev-env-indicator__env dev-env-indicator__env--${dbEnvironment}`}>
        {dbEnvironment} db
      </span>
      <span className="dev-env-indicator__divider" />
      <span className={`dev-env-indicator__dev-flag${!isDevServer ? ' dev-env-indicator__dev-flag--warning' : ''}`}>
        {!isDevServer && <i className="dev-env-indicator__warning-icon">⚠</i>}
        {isDevServer ? 'dev server' : 'NOT dev server'}
      </span>
    </div>
  );
};

export default DevEnvIndicator;
